import React, { PropsWithChildren, useMemo } from 'react';

import { ExternalLinkIcon } from '@chakra-ui/icons';
import { Box, BoxProps, Button, Flex, Heading, Image, Link, useTheme } from '@chakra-ui/react';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { useRouter } from 'next/router';
import { useCollectionDataOnce, useDocumentData } from 'react-firebase-hooks/firestore';
import { FaChevronLeft } from 'react-icons/fa';
import { FormattedMessage, useIntl } from 'react-intl';

import FeedbackButton from '~/components/Feedback/FeedbackButton';
import { useMicrositeConfig } from '~/components/Site/provider/MicrositeConfigProvider';
import SupportButton from '~/components/Support/SupportButton';
import { hasEventStarted } from '~/lib/data-transform/event';
import { hasScheduleOnDemandSources } from '~/lib/data-transform/event-schedules';
import { useEventAccess, useFirebaseApp, useLivestreamOnAir } from '~/lib/firebase';
import { getV2ContentUrl, getV2OnDemandUrl } from '~/lib/utils/booking';
import { isFomfTenant } from '~/lib/utils/tenant';

import ResponsiveMenu from './ResponsiveMenu';

export interface IHeaderProps extends BoxProps {
    userMenu?: React.ReactNode;
    appName?: string;
    showEventLinks?: boolean;
}

type EventsPageLinkProps = NextLinkProps & PropsWithChildren & { isExternal?: boolean };

const EventsPageLink: React.FunctionComponent<EventsPageLinkProps> = ({
    children,
    isExternal,
    ...props
}) => {
    const router = useRouter();
    const isPageActive =
        typeof props.href === 'object'
            ? router.pathname.startsWith(props.href.pathname || '')
            : false;
    return (
        <NextLink {...props} passHref>
            <Link
                isExternal={Boolean(isExternal)}
                display="inline-block"
                px="1"
                py="1"
                _hover={{ color: 'brand.500' }}
                borderBottom="3px solid"
                borderBottomColor={isPageActive ? 'highlight' : 'transparent'}
            >
                {children}
            </Link>
        </NextLink>
    );
};

const Header: React.FunctionComponent<IHeaderProps> = ({
    userMenu,
    appName,
    showEventLinks = true,
    ...props
}) => {
    const { app, userId } = useFirebaseApp();
    const { header, event, site } = useMicrositeConfig();
    const eventAccess = useEventAccess({ event });
    const [onAir] = useLivestreamOnAir(event?.id);
    const { formatMessage } = useIntl();

    const ref =
        event && userId ? app.firestore().collection(`/events/${event.id}/expo`).limit(1) : null;
    const [sponsors, loadingSponsors] = useCollectionDataOnce<{}>(ref);
    const [expoSettings, loadingExpoSettings] = useDocumentData(
        event && userId ? app.firestore().doc(`/events/${event.id}/site-config/expo`) : null
    );
    const isShowUserBookingsUrl = event
        ? typeof localStorage !== 'undefined'
            ? localStorage.getItem(`ext_url_${event.id}`) || event.ext_url
            : event.ext_url
        : '';

    const { logo } = useTheme();
    const logoUrl = logo || '/examples/logo.png';
    const canViewExhibitionMenuItem =
        !eventAccess.isLoading() &&
        eventAccess.canViewExhibition() &&
        !loadingSponsors &&
        !loadingExpoSettings &&
        event?.country !== 'GB' &&
        ((event && event.ext_expo_url) ||
            (sponsors && sponsors.length) ||
            event?.v2Content.sponsorships?.length ||
            (expoSettings && expoSettings.isPublic));
    const canJoinWonderRoom =
        event?.wonder_room_url &&
        !eventAccess.isLoading() &&
        eventAccess.canViewLivestream() &&
        onAir?.stream;

    const showEventTitleLinks =
        !eventAccess.isLoading() &&
        !eventAccess.isAccessLinkUser() &&
        isShowUserBookingsUrl &&
        showEventLinks;

    const { eventBookingId } = eventAccess.v2AccessData;
    const backButtonUrl = useMemo(() => {
        if (eventBookingId) {
            return `/user/dashboard/bookings/${eventBookingId}/booking-details`;
        }
        if (event?.country === 'GB') {
            return `/${event?.v2Content.slug}`;
        }
        return `/content/${event?.v2Content.slug}`;
    }, [eventBookingId, event]);

    const showEventPagesLinks = event && !eventAccess.isAccessLinkUser() && showEventLinks;

    const pageTitle =
        expoSettings?.page_title ||
        (isFomfTenant()
            ? formatMessage({ id: 'nav.main.partners', defaultMessage: 'Partners' })
            : formatMessage({ id: 'nav.main.exhibition', defaultMessage: 'Ausstellung' }));

    const ext_url = (event && getV2ContentUrl(event)) || '';

    const downloadUrl = eventBookingId
        ? `/user/dashboard/bookings/${eventBookingId}/downloads`
        : ext_url;

    return (
        <Box mx="auto" p="2" {...props} overflow="auto">
            <Flex align="center" justifyContent="space-between">
                {header.showLogo && site.logo ? (
                    <Image
                        my="2"
                        mr="8"
                        height="full"
                        maxH="50px"
                        width="auto"
                        src={logoUrl}
                        title={event?.title || ''}
                    />
                ) : null}

                {appName ? (
                    <Heading size="md" color="highlight">
                        {appName}
                    </Heading>
                ) : null}

                {showEventTitleLinks ? (
                    <Box pr={2}>
                        <Button
                            textAlign="left"
                            px="0"
                            variant="ghost"
                            colorScheme="brand"
                            as="a"
                            href={backButtonUrl}
                            leftIcon={<FaChevronLeft size="1.4em" />}
                        >
                            <Box
                                display={{ base: 'none', lg: 'inline' }}
                                as="span"
                                maxW="260px"
                                isTruncated
                            >
                                {event?.title}
                            </Box>
                        </Button>
                    </Box>
                ) : null}

                {showEventPagesLinks ? (
                    <Box
                        color="highlight"
                        textTransform="uppercase"
                        fontWeight="bold"
                        justifySelf="center"
                        px="2"
                    >
                        <ResponsiveMenu whiteSpace="nowrap">
                            {event.ondemand_redirect ? null : (
                                <Box>
                                    <EventsPageLink
                                        href={{
                                            pathname: '/events/[id]/livestream',
                                            query: { id: event.id }
                                        }}
                                    >
                                        <FormattedMessage
                                            id="nav.main.livestream"
                                            defaultMessage="Livestream"
                                        />
                                    </EventsPageLink>
                                </Box>
                            )}
                            {(hasScheduleOnDemandSources(event.event_schedules) ||
                                site.hostname === 'fomf.stream') &&
                                hasEventStarted(event) && (
                                    <Link
                                        isExternal
                                        display="inline-block"
                                        px="1"
                                        py="1"
                                        href={getV2OnDemandUrl(event)}
                                        _hover={{ color: 'brand.500' }}
                                    >
                                        <FormattedMessage
                                            id="nav.main.ondemand"
                                            defaultMessage="On-Demand"
                                        />
                                    </Link>
                                )}

                            {canViewExhibitionMenuItem ? (
                                <Box>
                                    <EventsPageLink
                                        href={
                                            event.ext_expo_url
                                                ? event.ext_expo_url
                                                : {
                                                      pathname: '/events/[id]/exhibition',
                                                      query: { id: event.id }
                                                  }
                                        }
                                    >
                                        {pageTitle}
                                    </EventsPageLink>
                                </Box>
                            ) : null}
                            {downloadUrl ? (
                                <Link
                                    href={downloadUrl}
                                    isExternal
                                    display="inline-block"
                                    px="1"
                                    py="1"
                                    _hover={{ color: 'brand.500' }}
                                >
                                    <FormattedMessage
                                        id="nav.main.downloads"
                                        defaultMessage="Downloads"
                                    />
                                </Link>
                            ) : null}
                            {canJoinWonderRoom ? (
                                <Box>
                                    <EventsPageLink
                                        href={event.wonder_room_url || ''}
                                        isExternal={true}
                                    >
                                        <FormattedMessage
                                            id="nav.main.wonder_room"
                                            defaultMessage="Virtueller Raum"
                                        />{' '}
                                        <ExternalLinkIcon mx="2px" />
                                    </EventsPageLink>
                                </Box>
                            ) : null}
                        </ResponsiveMenu>
                    </Box>
                ) : null}

                <Box ml="auto" justifySelf="flex-end">
                    {userMenu}
                </Box>

                {userId && event?.feedback_url && (
                    <Box mx="2">
                        <FeedbackButton redirectUrl={event.feedback_url} />
                    </Box>
                )}

                <Box mx="2">
                    <SupportButton />
                </Box>
            </Flex>
        </Box>
    );
};

export default Header;
